import { QueryClient } from "@tanstack/react-query";
import { ModelName, MutationMethodForModel } from "../../types";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for getting mutation defaults.
 *
 * @template Model - The Tellescope model name (must support mutation methods)
 * @template Method - The mutation method for the model
 */
export interface GetTellescopeMutationDefaultsArgs<
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
> {
  /** The Tellescope model name */
  model: Model;
  /** Mutation method name */
  method: Method;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
}

/**
 * Gets the default options for a specific Tellescope mutation.
 * Returns the default mutation options object. If no defaults have been set, returns an empty object.
 * Mutation defaults are used to set default configurations like retry, onSuccess, onError, etc.
 *
 * Mutation keys follow the same structure as query keys: [model, method]
 *
 * @param args - The get mutation defaults arguments
 * @returns The default mutation options
 *
 * @example
 * ```tsx
 * // Get mutation defaults for a method
 * const defaults = getTellescopeMutationDefaults({
 *   queryClient,
 *   model: "users",
 *   method: "createOne",
 * });
 *
 * console.log("Retry count:", defaults.retry);
 * console.log("On success handler:", defaults.onSuccess);
 * ```
 */
export const getTellescopeMutationDefaults = <Model extends ModelName, Method extends MutationMethodForModel<Model>>({
  model,
  method,
  queryClient,
}: GetTellescopeMutationDefaultsArgs<Model, Method>) => {
  // Use the same key structure as queries: [model, method]
  // Mutations don't include request params in the key
  const mutationKey = buildTellescopeQueryKey({ model, method });

  return queryClient.getMutationDefaults(mutationKey);
};
